import { useEffect, useMemo } from 'react';
import EntityType from '@/shared/enums/EntityType';
import ScopeType from '@/shared/enums/ScopeType';
import useWorker from '@/shared/hooks/useWorker';
import {
  usePropertyContext,
  useScenarioContext,
  useTagContext,
} from '@/shared/providers';
import useDesignStore from '@/components/DesignContainer/hooks/useDesignStore';
import getMapAllEntitiesWorker from '../utils/entityMaps/mapAllEntities.worker.get';

const getFilterIds = (filter) => {
  return filter?.ids?.size ? [...filter.ids] : [];
};

const useAggregateEntities = ({
  entityType = EntityType.ACTIVITY,
  filter,
  filterMetrics,
  matrix,
  order,
  useComparison,
}) => {
  const {
    scenario,
    snapshotEntityMap,
    comparisonScenario,
    comparisonScenarioEntityMap,
  } = useScenarioContext();
  const settings = useDesignStore((state) => state.settings);
  const activityState = settings?.activities?.state;
  const { tagMap } = useTagContext();
  const { propertyMap } = usePropertyContext();

  const workerInstance = useMemo(() => {
    return getMapAllEntitiesWorker();
  }, []);

  const { isLoading, result, run } = useWorker({
    worker: Worker.default,
    workerInstance,
  });

  const scopeFilter =
    filter[ScopeType.TAGS] ??
    filter[ScopeType.PROCESSES] ??
    filter[ScopeType.ACTIVITIES];
  const ids = getFilterIds(scopeFilter);

  useEffect(() => {
    if (!scenario?.entity || !tagMap.size) {
      return;
    }

    const primary = {
      activityState,
      entityType,
      filter: scopeFilter,
      filterMetrics,
      matrix,
      order,
      propertyMap,
      relationshipMap: scenario?.relationships,
      snapshotEntityMap,
      tagMap,
    };

    const comparison =
      useComparison && comparisonScenario
        ? {
            activityState,
            entityType,
            filter: scopeFilter,
            filterMetrics,
            matrix,
            order,
            propertyMap,
            relationshipMap: comparisonScenario?.relationships,
            snapshotEntityMap: comparisonScenarioEntityMap,
            tagMap,
          }
        : null;

    run({ primary, comparison });
  }, [
    filterMetrics,
    scenario?.entity?.uuid,
    scenario?.entity?.__uuid,
    scenario?.entity?.updated_at,
    JSON.stringify(scenario?.metrics?.visible),
    useComparison,
    comparisonScenario?.relationships,
    comparisonScenarioEntityMap,
    JSON.stringify(matrix),
    tagMap?.size,
    JSON.stringify(order),
    JSON.stringify(scopeFilter),
    JSON.stringify(ids),
  ]);

  return {
    entities: result?.entities,
    comparisonColor: comparisonScenario?.comparisonColor,
    isLoading,
  };
};

export default useAggregateEntities;
